'use client';

import {
  type ChangeEvent,
  type ChangeEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { useToggle } from '../../hooks/use-toggle';
import { ChipGroupContext } from '../chip-group/context/chip-group.context';
import { Slottable } from '../slot';
import { VisuallyHidden } from '../visually-hidden';
import { FilterChipWithMenuContext } from './context/filter-chip-with-menu.context';
import { filterChipWithMenuRootRecipe } from './styles/filter-chip-with-menu-root.styles';
import type {
  FilterChipWithMenuContextInterface,
  FilterChipWithMenuRootPropsInterface,
} from './types/filter-chip-with-menu-root.types';

/**
 * @since 10.0.0
 */

export const FilterChipWithMenuRoot = forwardRef<
  HTMLLabelElement,
  FilterChipWithMenuRootPropsInterface
>((props: FilterChipWithMenuRootPropsInterface, forwardedRef) => {
  const chipGroupContext = useContext(ChipGroupContext);

  const {
    children,
    className,
    css: cssProp,
    disabled = chipGroupContext.disabled || false,
    elevated = chipGroupContext.elevated || false,
    labelVoiceOver = 'Filter',
    value,
    defaultValue,
    onChange,
    ...rest
  } = props;

  // Determine if this chip is part of a group selection (only when group has selection)
  const hasGroupSelection = Boolean(chipGroupContext.handleSelection && chipGroupContext.selected);

  // Handle initial value, considering group selection only when relevant
  const initialValue = useMemo(() => {
    if (hasGroupSelection && typeof chipGroupContext.selected === 'string') {
      return chipGroupContext.selected;
    }
    return defaultValue;
  }, [hasGroupSelection, chipGroupContext.selected, defaultValue]);

  // If the value is controlled, use the value prop, otherwise use the initial value
  const [uncontrolledValue, setUncontrolledValue] = useState(initialValue);
  const isControlled = 'value' in props;
  const currentValue = isControlled ? value : uncontrolledValue;

  // Check if current value is selected in group (only when there's actual group selection)
  const isSelectedInGroup =
    hasGroupSelection && currentValue && chipGroupContext.isSelected?.(currentValue.toString());

  // Use original logic, enhanced only when there's group selection
  const shouldBeToggledOn = !!value || !!defaultValue || isSelectedInGroup || false;
  const [isToggledOn, setIsToggledOn] = useToggle(shouldBeToggledOn);

  const onValueChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const newValue = event.target.value;

      onChange?.(event);
      setIsToggledOn(!!newValue);

      if (!isControlled) {
        setUncontrolledValue(newValue);
      }

      // Update group selection only if group has selection capability
      if (chipGroupContext.handleSelection) {
        chipGroupContext.handleSelection(newValue);
      }
    },
    [onChange, setIsToggledOn, isControlled, chipGroupContext],
  );

  const finalIsToggledOn = isToggledOn || Boolean(isSelectedInGroup);

  const chipContext = useMemo<FilterChipWithMenuContextInterface>(
    () => ({
      disabled,
      elevated,
      isToggledOn: finalIsToggledOn,
      setIsToggledOn,
      value: currentValue,
      onChange: onValueChange,
    }),
    [disabled, elevated, finalIsToggledOn, setIsToggledOn, currentValue, onValueChange],
  );

  const styles = css(
    filterChipWithMenuRootRecipe.raw({
      disabled,
      isToggledOn: finalIsToggledOn,
      elevated,
    }),
    cssProp,
  );

  return (
    <FilterChipWithMenuContext.Provider value={chipContext}>
      <label
        ref={forwardedRef}
        data-c11n-component="FilterChipWithMenu.Root"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_filter-chip-with-menu__root', styles, className)}
        {...rest}
      >
        <VisuallyHidden>{labelVoiceOver}</VisuallyHidden>
        <Slottable>{children}</Slottable>
      </label>
    </FilterChipWithMenuContext.Provider>
  );
});

FilterChipWithMenuRoot.displayName = 'FilterChipWithMenu.Root';
